// Pure bidder-portal origin resolution for notification links (BL-303 / BUG-813).
//
// The single source of truth for "where does this tenant's bidder site live?",
// shared by BOTH notification engines (the legacy admin engine in
// `apps/admin-web/src/lib/notifications/` and the new `@auctogon/notifications`
// pipeline). Client-safe — pure string logic, no I/O, no `@auctogon/db` runtime
// dep — so it stays testable with no mocks and the two engines can't drift.
//
// The DB read that fetches the inputs lives in the server-only wrapper
// `@auctogon/notifications` → `resolveBidderOrigin`; this file owns only the
// precedence + normalization (the part worth standardizing + unit-testing).

/**
 * Normalize a tenant-supplied bidder host into a clean `https://<host>` origin.
 *
 * Accepts a bare host (`acrepro.auctogon.com`), a scheme-prefixed value
 * (`https://acrepro.auctogon.com` / `http://...`), and tolerates a trailing
 * slash. Returns `''` for null/empty so callers reliably hit the empty-string
 * branch and the email template renders plain bold fallback text instead of a
 * broken anchor.
 *
 * **TLS coercion:** an `http://` scheme is silently upgraded to `https://`.
 * Buyer-facing CTAs carry invoice/payment destinations — a misconfigured tenant
 * value must not produce a plaintext-HTTP link a network MITM could rewrite.
 * (BL-303 RAC 3.)
 *
 * **Loopback exemption (BL-1212):** `localhost` / `127.0.0.1` / `[::1]` keep
 * their `http://` scheme. That traffic never leaves the machine, so there is no
 * network path for the MITM this coercion defends against — and coercing it
 * produced a dead link on every local dev box (the local bidder app serves
 * plain HTTP on :3001), which is what the admin "View Site" affordance hit.
 * Narrow by construction: only these three literal hosts, optionally with a
 * port; any other host still coerces.
 *
 * Note: this normalizes the *origin* only. The path-based fallback in
 * {@link buildBidderOrigin} appends `/<slug>` AFTER normalization, so a value
 * like `https://app.auctogon.com/acme` passed back through here is returned
 * unchanged (idempotent) — the `buildBidder*` URL helpers can keep calling it.
 *
 * @param host Bare or scheme-prefixed host, or null/empty.
 * @returns `https://<host>` (or the input origin unchanged), or `''`.
 */
export function normalizeBidderOrigin(host: string | null | undefined): string {
  if (!host) return '';
  const trimmed = host.trim().replace(/\/+$/, '');
  if (!trimmed) return '';
  if (/^http:\/\//i.test(trimmed)) {
    const rest = trimmed.slice('http://'.length);
    // Loopback exemption (BL-1212) — see the note above. Anchored so a host
    // like `localhost.evil.com` or `127.0.0.1.attacker.net` does NOT match.
    if (/^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?(\/|$)/i.test(rest)) {
      return trimmed;
    }
    // Coerce to https — see TLS coercion note above.
    return `https://${rest}`;
  }
  if (/^https:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

/** Inputs to {@link buildBidderOrigin} — the three DB-sourced values + the env default. */
export interface BidderOriginParts {
  /** Tenant's primary custom domain from `tenant_domains` (host only), or null. */
  primaryDomain: string | null | undefined;
  /** Legacy `tenants.base_url` (host only, vanity host), or null. */
  baseUrl: string | null | undefined;
  /** `tenants.slug` — only used by the path-based env fallback. */
  slug: string | null | undefined;
  /** `NEXT_PUBLIC_BIDDER_BASE_URL` — the default bidder host, or null when unset. */
  envBase: string | null | undefined;
}

/**
 * Resolve a tenant's bidder-portal origin from its DB-sourced parts, in
 * priority order. **Never returns blank for a tenant with a custom domain,
 * a base_url, OR a slug-with-env-default** — only an all-unconfigured tenant
 * degrades to `''` (bold fallback text; BL-303 RAC 2).
 *
 * Precedence:
 *  1. **Custom domain** (`tenant_domains` primary) → `https://<domain>`.
 *     No slug suffix: the bidder middleware strips the `[tenant]` prefix for
 *     custom-domain hosts, so the portal lives at the host root.
 *  2. **Legacy `base_url`** → `https://<host>`. Same host-only shape as a
 *     custom domain (a vanity host); kept as back-compat for prod tenants
 *     configured before `tenant_domains` was the source of truth.
 *  3. **Env default + slug** → `https://<envBase-host>/<slug>`. The path-based
 *     scheme (`<bidder-host>/<tenant-slug>/...`) the bidder app uses for tenants
 *     WITHOUT a custom domain — the slug rides in the path, matching the admin
 *     "Preview tenant site" link.
 *  4. None resolvable → `''`.
 *
 * The returned origin is fully normalized (https, no trailing slash); callers
 * append their path directly.
 *
 * @param opts The DB-sourced parts + env default.
 * @returns A normalized `https://...` origin (optionally `/slug`-suffixed), or `''`.
 */
export function buildBidderOrigin(opts: BidderOriginParts): string {
  // 1. Custom domain wins — the standardization target.
  const domain = normalizeBidderOrigin(opts.primaryDomain);
  if (domain) return domain;

  // 2. Legacy vanity host (back-compat).
  const base = normalizeBidderOrigin(opts.baseUrl);
  if (base) return base;

  // 3. Path-based fallback: <env bidder host>/<slug>. Requires BOTH the env
  //    default and a slug — a slug with no env host (or vice versa) can't form
  //    a usable URL, so it degrades rather than emitting a half-built link.
  //    `tenants.slug` has only a NOT NULL + UNIQUE constraint (no format CHECK),
  //    so encode it as a single path segment to neutralize any path-traversal /
  //    reserved characters before it lands in a buyer-facing CTA.
  const envOrigin = normalizeBidderOrigin(opts.envBase);
  const slug = opts.slug?.trim();
  if (envOrigin && slug) return `${envOrigin}/${encodeURIComponent(slug)}`;

  // 4. Unconfigured — degrade to bold fallback text.
  return '';
}

/**
 * Resolve a tenant's bidder **host root** — the same precedence as
 * {@link buildBidderOrigin} but WITHOUT the `/<slug>` path segment that
 * precedence #3 appends. Use this for links to bidder routes that live at the
 * host root and are tenant-agnostic on the wire (the tenant is carried some
 * other way — e.g. a signed token). Today's only consumer is the no-login
 * unsubscribe pair (`/unsubscribe` + `/api/public/unsubscribe`, BL-242 AC 9):
 * both are ROOT routes (the middleware bypasses `/unsubscribe` and every
 * `/api/*` from tenant-prefix rewriting), so a slug-suffixed URL
 * (`/<slug>/unsubscribe`) would fall through the `[tenant]` catch-all and 404
 * for every tenant on the shared path-based host.
 *
 * Precedence 1 (custom domain) and 2 (`base_url`) already return a host root,
 * so they are identical to `buildBidderOrigin`; only the path-based env
 * fallback differs — it returns the bare `https://<envBase-host>` with no slug.
 *
 * @param opts The DB-sourced parts + env default (`slug` is ignored here).
 * @returns A normalized host-root `https://...` origin (never `/slug`-suffixed), or `''`.
 */
export function buildBidderRootOrigin(opts: BidderOriginParts): string {
  // 1. Custom domain — host root.
  const domain = normalizeBidderOrigin(opts.primaryDomain);
  if (domain) return domain;

  // 2. Legacy vanity host — host root.
  const base = normalizeBidderOrigin(opts.baseUrl);
  if (base) return base;

  // 3. Env default host root — NO slug (the distinguishing line vs
  //    buildBidderOrigin): the target routes are tenant-agnostic root routes.
  const envOrigin = normalizeBidderOrigin(opts.envBase);
  if (envOrigin) return envOrigin;

  // 4. Unconfigured.
  return '';
}
